/**
 * AdminPortalLayout — the global-admin portal shell (admin.karexia.com).
 *
 * A platform-operator surface that is NOT a tenant's own owner/admin console:
 * it is gated on the Keycloak `platform_superadmin` realm role exactly like the
 * cross-tenant FleetPage (useRoles()), rather than on the per-tenant `org_role`.
 * A non-superadmin caller never hits the network — the restricted-access panel
 * renders immediately and no section is mounted. The `<Outlet/>` renders the
 * active section (Fleet/Tenants, AI Credits & Margin, Models, Plans & Pricing,
 * Entitlements, Integrations & Vendor Keys, Platform Settings), each built as a
 * separate child page under `src/routes/admin/` by sibling agents.
 *
 * Bilingual FR/EN via the app's i18n locale store (useT().locale) — copy is
 * kept inline (a small FR/EN map) so the shell stays self-contained and does
 * not depend on new keys being added to the shared translation catalogs.
 *
 * Theme + primitives are reused from the shared kit (`@/ui`, settings/ui) so
 * the portal is dark-mode correct and visually consistent with the app.
 */
import { type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from 'react-oidc-context';

import { CopilotProvider } from '@/features/copilot';
import { useT } from '@/i18n';
import { isAdminHost } from '@/lib/adminPortal';
import { useRoles } from '@/routes/settings/useRoles';
import { Badge } from '@/routes/settings/ui';
import { GlobalConfirmHost } from '@/ui';
import { Toaster } from '@/ui/toast';

/** The stable, canonical section list — shared with AdminPortalHome so the
 *  sidebar and the landing dashboard never drift out of sync. `path` is the
 *  child route segment under `/admin`; `end` marks the index (home). */
export interface AdminSection {
  /** Child route segment under `/admin` (empty string = the index/home). */
  path: string;
  /** Stable id used for keys + icon lookup. */
  id: string;
  /** Bilingual label. */
  label: { fr: string; en: string; ar: string };
  /** Bilingual one-line description (used on the home cards). */
  desc: { fr: string; en: string; ar: string };
  /**
   * Legacy emoji glyph. Retained only for backward compatibility with
   * AdminPortalHome's cards; the portal shell itself now renders `icon`.
   */
  glyph: string;
  /** Inline stroke-SVG path(s) — the shared app icon language. */
  icon: ReactNode;
}

// Inline stroke-SVG icon (zero-dep) in the same 24×24 / strokeWidth-1.8 visual
// language as the app sidebar, so the admin portal speaks one icon language
// instead of emoji glyphs.
function AdminIcon({ path, className = 'h-5 w-5' }: { path: ReactNode; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${className} flex-none`}
    >
      {path}
    </svg>
  );
}

const GEAR_PATH = (
  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.7 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9.4A1.6 1.6 0 0 0 11 3.1V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9.4a1.6 1.6 0 0 0 1.7 1.6H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
);

export const ADMIN_SECTIONS: AdminSection[] = [
  {
    path: 'fleet',
    id: 'fleet',
    label: { fr: 'Flotte / Tenants', en: 'Fleet / Tenants', ar: 'الأسطول / المستأجرون' },
    desc: {
      fr: 'Vue cross-tenant de la flotte 24/7 et des organisations de la plateforme.',
      en: 'Cross-tenant view of the 24/7 fleet and every platform organization.',
      ar: 'عرض عابر للمستأجرين لأسطول 24/7 وكل مؤسسات المنصة.',
    },
    glyph: '🛰️',
    icon: (
      <path d="M7 12a5 5 0 0 1 5-5m0 10a5 5 0 0 0 5-5M6 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
    ),
  },
  {
    path: 'credits',
    id: 'credits',
    label: { fr: 'Crédits IA & marge', en: 'AI Credits & Margin', ar: 'أرصدة الذكاء الاصطناعي والهامش' },
    desc: {
      fr: 'Marge brute des crédits IA (par modèle + défaut global) et consommation.',
      en: 'AI-credit gross margin (per-model + global default) and consumption.',
      ar: 'الهامش الإجمالي لأرصدة الذكاء الاصطناعي (لكل نموذج + الافتراضي العام) والاستهلاك.',
    },
    glyph: '💳',
    icon: <path d="M3 7h18v10H3zM3 11h18M7 15h4" />,
  },
  {
    path: 'models',
    id: 'models',
    label: { fr: 'Modèles', en: 'Models', ar: 'النماذج' },
    desc: {
      fr: 'Catalogue des modèles LLM, coûts, multiplicateurs et disponibilité.',
      en: 'LLM model catalogue, costs, multipliers and availability.',
      ar: 'كتالوج نماذج LLM والتكاليف والمضاعفات والتوفر.',
    },
    glyph: '🧠',
    icon: (
      <path d="M9 3a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8A3 3 0 0 0 6 17a3 3 0 0 0 6 .9V6a3 3 0 0 0-3-3zM15 3a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8A3 3 0 0 1 18 17a3 3 0 0 1-6 .9" />
    ),
  },
  {
    path: 'plans',
    id: 'plans',
    label: { fr: 'Plans & tarification', en: 'Plans & Pricing', ar: 'الخطط والتسعير' },
    desc: {
      fr: 'Plans d’abonnement, paliers de prix et quotas par plan.',
      en: 'Subscription plans, price tiers and per-plan quotas.',
      ar: 'خطط الاشتراك وفئات الأسعار والحصص لكل خطة.',
    },
    glyph: '🏷️',
    icon: <path d="M4 4h8l8 8-8 8-8-8zM8.5 8.5h.01" />,
  },
  {
    path: 'vouchers',
    id: 'vouchers',
    label: { fr: 'Bons & codes promo', en: 'Vouchers', ar: 'القسائم' },
    desc: {
      fr: 'Émettre et désactiver les bons de crédits IA.',
      en: 'Mint and deactivate AI-credit vouchers.',
      ar: 'إصدار قسائم أرصدة الذكاء الاصطناعي وتعطيلها.',
    },
    glyph: '🎟️',
    icon: <path d="M3 8a2 2 0 0 0 0 4v4h18v-4a2 2 0 0 1 0-4V4H3zM13 4v16" />,
  },
  {
    path: 'entitlements',
    id: 'entitlements',
    label: { fr: 'Droits (entitlements)', en: 'Entitlements', ar: 'الاستحقاقات' },
    desc: {
      fr: 'Fonctionnalités activées par plan et surcharges par tenant.',
      en: 'Feature entitlements per plan and per-tenant overrides.',
      ar: 'استحقاقات الميزات لكل خطة والتجاوزات لكل مستأجر.',
    },
    glyph: '🔑',
    icon: <path d="M15 7a4 4 0 1 0-3.9 5L4 19v2h3v-2h2v-2h2l1.1-1.1A4 4 0 0 0 15 7zM16.5 8.5h.01" />,
  },
  {
    path: 'integrations',
    id: 'integrations',
    label: { fr: 'Intégrations & clés fournisseurs', en: 'Integrations & Vendor Keys', ar: 'التكاملات ومفاتيح المزوّدين' },
    desc: {
      fr: 'Clés fournisseurs plateforme et connecteurs (jamais affichées en clair).',
      en: 'Platform vendor keys and connectors (never surfaced in clear text).',
      ar: 'مفاتيح مزوّدي المنصة والموصلات (لا تُعرض أبدًا كنص واضح).',
    },
    glyph: '🔌',
    icon: <path d="M9 7V3M15 7V3M7 7h10v4a5 5 0 0 1-10 0zM12 16v5" />,
  },
  {
    path: 'communities',
    id: 'communities',
    label: { fr: 'Communautés de contenu', en: 'Content Communities', ar: 'مجتمعات المحتوى' },
    desc: {
      fr: 'Activation globale et valeurs par défaut de l’engagement (approbation par défaut).',
      en: 'Global activation and org-wide engagement defaults (approval by default).',
      ar: 'التفعيل العام والقيم الافتراضية للتفاعل على مستوى المؤسسة (الموافقة افتراضيًا).',
    },
    glyph: '🤝',
    icon: (
      <path d="M17 20h5v-2a3 3 0 0 0-5.4-1.8M7 20H2v-2a3 3 0 0 1 5.4-1.8M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 9a2.5 2.5 0 1 0 0-5M19 9a2.5 2.5 0 1 1 0-5" />
    ),
  },
  {
    path: 'privacy',
    id: 'privacy',
    label: { fr: 'Confidentialité & conformité', en: 'Privacy & Compliance', ar: 'الخصوصية والامتثال' },
    desc: {
      fr: 'Demandes d’accès/effacement (Loi 25 · RGPD), rétention des données et audit unifié.',
      en: 'Data-subject requests (Law 25 · GDPR), data retention and a unified audit log.',
      ar: 'طلبات أصحاب البيانات (القانون 25 · GDPR)، والاحتفاظ بالبيانات، وسجل تدقيق موحد.',
    },
    glyph: '🛡️',
    icon: <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3zM9.5 12l1.8 1.8L15 9.8" />,
  },
  {
    // R18 — consent-gated review queue feeding the public /results page.
    path: 'case-studies',
    id: 'case-studies',
    label: { fr: 'Études de cas clients', en: 'Customer Case Studies', ar: 'دراسات حالة العملاء' },
    desc: {
      fr: 'Relecture des études de cas soumises avec consentement écrit, avant publication sur /results.',
      en: 'Review customer case studies submitted with written consent before they go live on /results.',
      ar: 'مراجعة دراسات حالة العملاء المقدمة بموافقة كتابية قبل نشرها على /results.',
    },
    glyph: '🏆',
    icon: <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM17 6h3v2a3 3 0 0 1-3 3M7 6H4v2a3 3 0 0 0 3 3" />,
  },
  {
    // I8 — in-app human support inbox (all tenants): reply, status, assignment.
    path: 'support',
    id: 'support',
    label: { fr: 'Boîte de support', en: 'Support inbox', ar: 'صندوق الدعم' },
    desc: {
      fr: 'Demandes des utilisateurs envoyées depuis l’application : répondre, assigner, changer le statut.',
      en: 'User requests sent from inside the app: reply, assign, change status.',
      ar: 'طلبات المستخدمين المرسلة من داخل التطبيق: الرد والتعيين وتغيير الحالة.',
    },
    glyph: '🛟',
    icon: <path d="M4 13a8 8 0 0 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 2zM20 13v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2zM17 18c0 1.7-2.2 3-5 3" />,
  },
  {
    path: 'settings',
    id: 'settings',
    label: { fr: 'Paramètres plateforme', en: 'Platform Settings', ar: 'إعدادات المنصة' },
    desc: {
      fr: 'Réglages globaux de la plateforme et bascules opérateur.',
      en: 'Global platform configuration and operator toggles.',
      ar: 'الإعداد العام للمنصة ومفاتيح المشغّل.',
    },
    glyph: '⚙️',
    icon: GEAR_PATH,
  },
];

const COPY = {
  title: { fr: 'Console d’administration', en: 'Global Admin Console', ar: 'وحدة تحكم المسؤول العام' },
  subtitle: {
    fr: 'Opérations plateforme — cross-tenant, réservé au superadmin.',
    en: 'Platform operations — cross-tenant, superadmin only.',
    ar: 'عمليات المنصة — عابرة للمستأجرين، للمسؤول الأعلى فقط.',
  },
  backToApp: { fr: '← Retour à l’application', en: '← Back to app', ar: '→ العودة إلى التطبيق' },
  platformAdmin: { fr: 'Admin plateforme', en: 'Platform admin', ar: 'إدارة المنصة' },
  restrictedTitle: {
    fr: 'Accès réservé au superadmin plateforme',
    en: 'Platform-superadmin access only',
    ar: 'وصول مقتصر على المسؤول الأعلى للمنصة',
  },
  restrictedBody: {
    fr: 'Cette console est réservée aux opérateurs de la plateforme (rôle platform_superadmin). Elle n’est pas liée au rôle propriétaire/administrateur de votre organisation — contactez un superadmin de la plateforme si vous pensez devoir y accéder.',
    en: 'This console is reserved for platform operators (the platform_superadmin realm role). It is not tied to your organization’s owner/admin role — contact a platform superadmin if you believe you should have access.',
    ar: 'وحدة التحكم هذه مقتصرة على مشغّلي المنصة (دور النطاق platform_superadmin). وهي غير مرتبطة بدور المالك/المسؤول في مؤسستك — تواصل مع مسؤول أعلى للمنصة إذا كنت تعتقد أنه يجب أن يكون لديك وصول.',
  },
  nav: { fr: 'Sections', en: 'Sections', ar: 'الأقسام' },
} as const;

export function AdminPortalLayout() {
  const { roles } = useRoles();
  const isSuperadmin = roles.includes('platform_superadmin');
  const { locale } = useT();
  const auth = useAuth();
  // FR is the source language; anything other than EN falls back to FR copy.
  const L = locale;

  const displayName =
    auth.user?.profile?.name ??
    auth.user?.profile?.preferred_username ??
    auth.user?.profile?.email ??
    COPY.platformAdmin[L];

  /* ------------------------------ gating ------------------------------- */
  // Mirror FleetPage: a non-superadmin renders the restricted panel and NO
  // section is mounted (the child routes/<Outlet/> never render → no network).
  if (!isSuperadmin) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-bg p-6 text-foreground">
        <div className="w-full max-w-lg rounded-token-lg border border-border bg-surface p-8 text-center shadow-elevation-1">
          <AdminIcon
            path={<path d="M6 10V8a6 6 0 0 1 12 0v2M5 10h14v10H5zM12 15v2" />}
            className="mx-auto mb-3 h-8 w-8 text-muted"
          />
          <p className="text-base font-semibold text-foreground">{COPY.restrictedTitle[L]}</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted">{COPY.restrictedBody[L]}</p>
          <a
            href="/"
            className="mt-5 inline-flex items-center justify-center rounded-token border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2"
          >
            {COPY.backToApp[L]}
          </a>
        </div>
      </div>
    );
  }

  /* ------------------------------- shell ------------------------------- */
  // On the dedicated admin subdomain "back to app" points at the tenant host;
  // when the portal is reached on the normal app host, a same-origin link home
  // is correct. Host-only branch (no path coupling) via isAdminHost().
  const backToAppHref = isAdminHost() ? 'https://www.karexia.com/' : '/';

  return (
    <div className="flex min-h-screen w-full bg-bg text-foreground">
      {/* Left sidebar */}
      <aside className="hidden w-64 flex-none flex-col border-r border-border bg-surface md:flex">
        <div className="flex h-14 flex-none items-center gap-2 border-b border-border px-4">
          <AdminIcon path={GEAR_PATH} className="h-5 w-5 text-foreground" />
          <span className="truncate text-sm font-semibold text-foreground">{COPY.title[L]}</span>
        </div>
        <nav aria-label={COPY.nav[L]} className="flex-1 overflow-y-auto p-2">
          <ul className="flex flex-col gap-0.5">
            {ADMIN_SECTIONS.map((s) => (
              <li key={s.id}>
                <NavLink
                  to={`/admin/${s.path}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-token border-l-2 px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      isActive
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-transparent text-foreground hover:bg-surface-2'
                    }`
                  }
                >
                  <AdminIcon path={s.icon} className="h-5 w-5" />
                  <span className="truncate">{s.label[L]}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-14 flex-none items-center justify-between gap-3 border-b border-border bg-surface px-4">
          <div className="flex min-w-0 items-center gap-3">
            <a
              href={backToAppHref}
              className="whitespace-nowrap text-sm font-medium text-muted transition hover:text-foreground"
            >
              {COPY.backToApp[L]}
            </a>
            <span className="hidden text-xs text-muted sm:inline">·</span>
            <span className="hidden truncate text-xs text-muted sm:inline">{COPY.subtitle[L]}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="border border-primary/30 bg-primary/10 text-primary">
              {COPY.platformAdmin[L]}
            </Badge>
            <span className="max-w-[12rem] truncate text-sm font-medium text-foreground" title={displayName}>
              {displayName}
            </span>
          </div>
        </header>

        {/* Mobile section nav (sidebar is hidden < md) */}
        <div className="flex gap-1 overflow-x-auto border-b border-border bg-surface px-3 py-2 md:hidden">
          {ADMIN_SECTIONS.map((s) => (
            <NavLink
              key={s.id}
              to={`/admin/${s.path}`}
              className={({ isActive }) =>
                `-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-1 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isActive
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-transparent text-muted hover:bg-surface-2 hover:text-foreground'
                }`
              }
            >
              <AdminIcon path={s.icon} className="h-4 w-4" />
              {s.label[L]}
            </NavLink>
          ))}
        </div>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
      {/* The console is its own shell (NOT DashboardLayout), so it must mount the
          global toast + confirm hosts itself — otherwise every section's
          success/error toast is silently dropped and confirmAsync() degrades to
          the browser's native window.confirm. */}
      <Toaster />
      <GlobalConfirmHost />
      {/* Copilot: console forms register with useCopilotForm (e.g. the manual
          top-up « credit-manual-topup ») — without the provider mounted in this
          shell the copilot could no longer fill or submit them. */}
      <CopilotProvider />
    </div>
  );
}
